/** @jsx jsx */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Button, jsx, Text } from 'theme-ui'

const StoreLocatorList = ({
  activeLocation,
  currentCenter,
  defaultCenter,
  distanceThreshold,
  getDistanceFromCurrentCenter,
  locations,
  setActiveLocation,
  setError,
  setLocations,
  styles,
  ui,
}) => {
  const [displayLocations, setDisplayLocations] = useState(null)
  const listItems = useRef([])

  // Updates location distances from the current center.
  const handlePlaceChanged = useCallback(
    async () =>
      setLocations(
        locations
          .map((location) => {
            const distance = getDistanceFromCurrentCenter(location.coords)

            return {
              ...location,
              distance: {
                text: `${distance.toFixed(1)}mi`,
                value: distance,
              },
            }
          })
          .sort((a, b) => a?.distance?.value - b?.distance?.value)
      ),
    [currentCenter] // eslint-disable-line
  )

  // Scrolls to the active list item.
  useEffect(() => {
    if (!listItems.current?.[activeLocation?.id]) return

    listItems.current?.[activeLocation.id].scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    })
  }, [activeLocation])

  // Triggers distance calculations on initialization and searches.
  useEffect(() => {
    if (!currentCenter || defaultCenter === currentCenter) return

    handlePlaceChanged()
  }, [currentCenter]) // eslint-disable-line

  useEffect(() => {
    setError(null)

    if (!locations?.[0]?.distance?.value) {
      setDisplayLocations(locations)
    } else if (locations[0].distance.value < distanceThreshold) {
      setDisplayLocations(
        locations.filter(
          (location) => location.distance.value < distanceThreshold
        )
      )
    } else {
      setDisplayLocations(null)
      setError(`There are no locations within ${distanceThreshold} miles.`)
    }
  }, [locations]) // eslint-disable-line

  return (
    <Box
      sx={{
        minHeight: '100%',
        ...styles?.list?.container,
      }}
    >
      {displayLocations &&
        displayLocations.map((location) => (
          <StoreLocatorListItem
            isActive={activeLocation?.id === location.id}
            key={`location-${location.id}`}
            listItems={listItems}
            location={location}
            setActiveLocation={setActiveLocation}
            styles={styles}
            ui={ui}
          />
        ))}
    </Box>
  )
}

const StoreLocatorListItem = ({
  isActive,
  listItems,
  location,
  setActiveLocation,
  styles,
  ui,
}) => {
  const { address1, address2, distance, id, title } = location

  return (
    <Button
      variant="reset"
      ref={(el) => (listItems.current[id] = el)}
      onClick={() => setActiveLocation(!isActive ? location : null)}
      sx={{
        borderBottom: '1px solid #F0F0F1',
        display: 'flex',
        cursor: 'pointer',
        py: 1,
        textAlign: 'left',
        width: '100%',
        '&:last-child': {
          borderBottom: 'none',
        },
        ...styles?.list?.item?.container,
      }}
    >
      {ui?.list?.marker?.icon && (
        <Box
          sx={{
            fill:
              isActive && ui?.map?.marker?.iconActive?.fillColor
                ? ui?.map?.marker?.iconActive?.fillColor
                : ui?.map?.marker?.icon?.fillColor,
            flexShrink: 0,
            mr: 1,
            position: 'relative',
            ...styles?.list?.item?.marker,
          }}
        >
          {ui.list.marker.icon}
        </Box>
      )}
      <Box sx={{ flexGrow: 1, ...styles?.list?.item?.text }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            ...styles?.list?.item?.label,
          }}
        >
          <Text as="strong" sx={{ ...styles?.list?.item?.title }}>
            {title}
          </Text>
          {distance && (
            <Text
              as="p"
              sx={{
                flexShrink: 0,
                ml: 1,
                ...styles?.list?.item?.distance,
              }}
            >
              {distance.text}
            </Text>
          )}
        </Box>
        <Box>
          <Text as="p" sx={{ my: 1, ...styles?.list?.item?.address }}>
            {address1}
            <br />
            {address2}
          </Text>
        </Box>
      </Box>
    </Button>
  )
}

export default StoreLocatorList
